import { useMemo } from 'react';
import { capitalizeFirstLetter, Field, FieldTypes } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { DEFAULT_DATE_FORMAT, REQUIRED_TEXT, SSN_INVALID_TEXT, SSN_MASK_WITH_DASHES } from '@/common/constants';
import { Dependent } from '@/modules/employee/employee.types';
import { useGetRelationshipListQuery } from '@/modules/transaction/CardDetails/AddCardDependentModal/queries/useGetRelationshipList.query';

import { DEPENDENT_SSN_SECURE_MASK, isValidDependentSsn } from '../dependents.utils';

const TODAY = dayjs().toDate();

enum DependentFields {
  firstName = 'firstName',
  lastName = 'lastName',
  birthOn = 'birthOn',
  ssn = 'ssn',
  relationshipType = 'relationshipType',
}

const useAddDependentFields = (dependent?: Dependent) => {
  const { relationshipList } = useGetRelationshipListQuery();
  const fields = useMemo(() => ([
    {
      name: DependentFields.firstName,
      type: FieldTypes.Text,
      label: 'First name',
      placeholder: 'Enter first name',
      showRequireIcon: true,
      validator: yup.string().required(REQUIRED_TEXT),
      isWide: true,
      defaultValue: dependent?.firstName,
    },
    {
      name: DependentFields.lastName,
      type: FieldTypes.Text,
      label: 'Last name',
      placeholder: 'Enter last name',
      showRequireIcon: true,
      validator: yup.string().required(REQUIRED_TEXT),
      isWide: true,
      defaultValue: dependent?.lastName,
    },
    {
      name: DependentFields.birthOn,
      type: FieldTypes.Date,
      label: 'Date of birth',
      placeholder: 'Select birth date',
      showRequireIcon: true,
      isManualDateInput: true,
      maxDate: TODAY,
      validator: VALIDATORS.DATE_FORMAT_MAX_TODAY.required(REQUIRED_TEXT),
      isCalendar: true,
      ...dependent?.birthOn ? {
        defaultValue: dayjs(dependent.birthOn).format(DEFAULT_DATE_FORMAT),
      } : {},
    },
    {
      name: DependentFields.ssn,
      type: FieldTypes.Mask,
      label: 'SSN',
      placeholder: 'Enter SSN',
      mask: SSN_MASK_WITH_DASHES,
      secure: true,
      secureMask: DEPENDENT_SSN_SECURE_MASK,
      formatChars: { '*': /[*]/ },
      clearOnFocus: true,
      validator: yup.string().test('ssn', SSN_INVALID_TEXT, isValidDependentSsn),
      isWide: true,
      defaultValue: dependent?.ssn,
    },
    {
      name: DependentFields.relationshipType,
      type: FieldTypes.Dropdown,
      label: 'Relationship',
      singleMode: true,
      placeholder: 'Select relationship',
      showRequireIcon: true,
      options: relationshipList.map((relationship) => ({
        key: `${relationship.value}`,
        value: `${relationship.value}`,
        title: capitalizeFirstLetter(relationship.name),
      })),
      validator: yup.string().required(REQUIRED_TEXT),
      defaultValue: dependent?.relationshipType,
    },
  ] as Field[]), [relationshipList, dependent]);

  return fields;
};

export default useAddDependentFields;
